import urls from "../../api/urls";
import NormalAPI from "../../api";

const {
  REPORT_CONFIG_LIST,
  REPORT_CONFIG_UPSERT,
  REPORT_CONFIG_PATCH,
  REPORT_CONFIG_TOGGLE_ACTIVE,
  REPORT_CONFIG_DELETE,
} = urls;

// ── Helpers ───────────────────────────────────────────────────────────────────

const unwrap = (wrapped) => ({
  status: wrapped?.response?.status ?? 0,
  data: wrapped?.response?.data ?? {},
});

const handleResponse = (wrapped, okCodes, fallbackError) => {
  const { status, data } = unwrap(wrapped);
  if (okCodes.includes(status)) {
    return data;
  }
  throw new Error(data?.message || data?.error || fallbackError);
};

// ── Service ───────────────────────────────────────────────────────────────────

export const ReportConfigService = {
  getAll: async (page = 0, size = 10) => {
    const url = `${REPORT_CONFIG_LIST}?page=${page}&size=${size}`;
    try {
      const wrapped = await NormalAPI.Api(url).getCustomHeader();
      const { status, data: d } = unwrap(wrapped);

      if (status === 200) {
        return {
          data: d?.data || d?.content || [],
          pagination: d?.pagination || {
            currentPage: page,
            totalPages: d?.totalPages || 1,
            totalRecords: d?.totalElements || (d?.data || d?.content || []).length,
            pageSize: size,
            hasNext: d?.hasNext ?? false,
            hasPrevious: d?.hasPrevious ?? false,
          },
        };
      }

      throw new Error(d?.message || "Failed to fetch report configs");
    } catch (error) {
      console.error("Error fetching report configs:", error);
      throw error;
    }
  },

  upsert: async (payload) => {
    try {
      const wrapped = await NormalAPI.Api(REPORT_CONFIG_UPSERT).postWithHeader(payload);
      return handleResponse(wrapped, [200, 201], "Save failed");
    } catch (error) {
      console.error("Error upserting report config:", error);
      throw error;
    }
  },

  patch: async (reportKey, fields) => {
    const url = `${REPORT_CONFIG_PATCH}/${reportKey}`;
    try {
      const wrapped = await NormalAPI.Api(url).patch(fields);
      return handleResponse(wrapped, [200], "Patch failed");
    } catch (error) {
      console.error("Error patching report config:", error);
      throw error;
    }
  },

  toggleActive: async (reportKey) => {
    const url = `${REPORT_CONFIG_TOGGLE_ACTIVE}/${reportKey}/toggle-active`;
    try {
      const wrapped = await NormalAPI.Api(url).patch({});
      return handleResponse(wrapped, [200], "Toggle failed");
    } catch (error) {
      console.error("Error toggling report config:", error);
      throw error;
    }
  },

  delete: async (reportKey) => {
    const url = `${REPORT_CONFIG_DELETE}/${reportKey}`;
    try {
      const wrapped = await NormalAPI.Api(url).delete({});
      handleResponse(wrapped, [200, 204], "Delete failed");
      return true;
    } catch (error) {
      console.error("Error deleting report config:", error);
      throw error;
    }
  },
};
